// The two sides of Autopilot, shared by Product settings > Autopilot and
// Video settings > Autopilot: the Product page card (the product's pages,
// each with its own autopilot switch, tag state and install snippet) and
// the Video card (the product's videos, each with its own switch — a video
// on autopilot has its channels automated). `onlyVideoId` narrows the
// Video card to one video (the video settings modal shows just itself).
import React, { useEffect, useRef, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import toast from 'react-hot-toast';
import axiosInstant from '../../../../api';
import { productAutopilotPageEndpoint, productAutopilotVideoEndpoint, productAutopilotVideoSelectionsEndpoint } from '../../../../api/endpoints';
import { PdpAutopilotDialog } from '../../../../components/PdpPages/PdpAutopilotDialog';
import { useUser } from '../../../../hooks/useUser';
import { usePortalPath } from '../../../../hooks/usePortalPath';
import { isInternal } from '../../../../utils';
import { apiErrorCode } from '../../../../utils/apiError';
import { getVideoThumbnail } from '../../../../utils/downloadHelpers';
import { IconChevronDown } from '../../../../utils/icons';
import { PdpChecksStrip, PdpPixelPill, PdpTagPill } from '../../../../components/PdpPages/PdpChecks';
import { TabHeader } from '../../../../components/Tabs/TabHeader';
import { BetaPill } from '../../../../components/BetaPill';
import { formatVideoName } from '../../videos/hooks/useFetchVideos';
import { pdpVideoHref } from '../../videos/selectedVideoCardV2/pdpAssignment';
import {
  AutopilotChannel,
  AutopilotPage,
  AutopilotSelection,
  AutopilotVideo,
  AutopilotVideoCategory,
  AutopilotVideoChannel,
  ProductAutopilot,
} from '../../../../types/autopilot';
import { Pill, btnSecondary, fmtDate, fmtNum } from './autopilotLabels';

// A "retailer tag" only when the page's tag brand is an enterprise network
// (RMN / agency plan). Free and standard brands install the same tag with
// no retailer framing, whoever owns the host.
const needsRetailerTag = (page: AutopilotPage) => !!(page.tag_brand && page.tag_brand.network) && !page.brand_owned;

// Whose tag a page needs, for the pill / install copy.
const tagOwnerLabel = (page: AutopilotPage) => {
  if (!needsRetailerTag(page)) return 'your site';
  return page.tag_brand ? `${page.tag_brand.name}'s site` : "the retailer's site";
};

const RETAILER_TAG_HELP =
  "A retailer page carries the script tag too — it just has to be installed on the retailer's site. Until it is, the page is measured by analysis and Consumer Voice only.";

// " · checking daily" while a change the brand accepted is awaited.
const cadenceNote = (page: AutopilotPage): string => {
  if (page.probe_interval_days === 1) return ' · checking daily';
  if (page.probe_interval_days === 0) return ' · page checks off';
  return '';
};

const modePill = (page: AutopilotPage) => {
  if (page.mode === 'on') return <Pill tone="good" title="The script tag is live on this page">Tag installed</Pill>;
  if (!needsRetailerTag(page)) {
    return <Pill tone="warn" title="The script tag has not been seen on this page yet">Needs tag</Pill>;
  }
  return (
    <Tooltip title={RETAILER_TAG_HELP}>
      <span>
        <Pill tone="warn">Needs retailer tag</Pill>
      </span>
    </Tooltip>
  );
};

// One side of autopilot: the product page (its pages, each with a switch)
// or the video (the product's videos, each with a switch — a video on
// autopilot has its channels automated).
function ChannelCard({ channel, subtitle, children }: { channel: AutopilotChannel; subtitle?: React.ReactNode; children?: React.ReactNode }) {
  const live = channel.enabled;
  return (
    <Box
      className={classNames(
        'flex flex-col gap-2 rounded-[12px] border p-3 min-w-0',
        live ? 'border-border-token bg-surface' : 'border-dashed border-border-token bg-surface-2 opacity-70',
      )}
    >
      <Box className="flex items-center justify-between gap-2">
        <span className={classNames('text-[13px] font-semibold', live ? 'text-text-1' : 'text-muted')}>{channel.name}</span>
        {/* Only a channel Autopilot does not run yet is marked. */}
        {live ? null : <Pill tone="neutral">Coming soon</Pill>}
      </Box>
      {subtitle ? <span className="text-[12px] text-text-2">{subtitle}</span> : null}
      {live ? children : null}
    </Box>
  );
}

const fmtLength = (seconds: number | null) => {
  if (!seconds || seconds <= 0) return null;
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// A checkbox that can show the "some of my children are off" state.
function NodeCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !checked ? false : indeterminate;
  }, [checked, indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      aria-label={label}
      className="h-[14px] w-[14px] shrink-0 accent-[var(--accent)] cursor-pointer disabled:cursor-not-allowed"
    />
  );
}

// A child row of the flow: a short connector from the parent's spine.
const Connector = () => <span className="inline-block w-[12px] h-px bg-border-token shrink-0 -ml-[13px] mr-[6px]" aria-hidden />;

// The channels Autopilot may automate for one video, as a flow per channel:
// Channel → placement category → placement, each with its own checkbox.
// Available is not on: everything starts unchecked; checking a node turns
// its subtree on, unchecking a child prunes it, and a parent switched off
// greys and disables its subtree.
function VideoChannelTree({
  channels,
  saving,
  onSelect,
}: {
  channels: AutopilotVideoChannel[];
  saving: boolean;
  onSelect: (sel: AutopilotSelection) => void;
}) {
  if (!channels.length) {
    return (
      <span className="text-[12px] text-muted">
        No channels are available for this video yet.
      </span>
    );
  }
  const catLabel = (ch: AutopilotVideoChannel, cat: AutopilotVideoCategory) => `${ch.name} › ${cat.name}`;
  return (
    <Box className="flex flex-col gap-3">
      {channels.map((ch) => {
        const catsOn = ch.categories.filter((c) => c.enabled).length;
        const chPartial = ch.categories.length > 0 && catsOn > 0 && catsOn < ch.categories.length;
        return (
          <Box key={ch.id} className="flex flex-col gap-1 min-w-0">
            <label className={classNames('inline-flex items-center gap-2 text-[12.5px] font-semibold', ch.enabled ? 'text-text-1' : 'text-muted')}>
              <NodeCheckbox
                checked={ch.enabled}
                indeterminate={chPartial}
                disabled={saving}
                label={ch.name}
                onChange={(checked) => onSelect({ kind: 'channel', content_channel_id: ch.id, ref_id: ch.id, enabled: checked })}
              />
              {ch.name}
              {!ch.categories.length ? <span className="text-[11px] font-normal text-muted">no placements open yet</span> : null}
            </label>
            {ch.categories.length ? (
              <Box className={classNames('ml-[6px] pl-[13px] border-l border-border-token flex flex-col gap-1', !ch.active && 'opacity-50')}>
                {ch.categories.map((cat) => {
                  const plOn = cat.placements.filter((p) => p.enabled).length;
                  const catPartial = cat.placements.length > 0 && plOn > 0 && plOn < cat.placements.length;
                  return (
                    <Box key={cat.id} className="flex flex-col gap-1 min-w-0">
                      <label className={classNames('inline-flex items-center text-[12px] font-semibold', cat.enabled ? 'text-text-1' : 'text-muted')}>
                        <Connector />
                        <NodeCheckbox
                          checked={cat.enabled}
                          indeterminate={catPartial}
                          disabled={saving || !ch.active}
                          label={catLabel(ch, cat)}
                          onChange={(checked) => onSelect({ kind: 'category', content_channel_id: ch.id, ref_id: cat.id, enabled: checked })}
                        />
                        <span className="ml-2 truncate">{cat.name}</span>
                      </label>
                      <Box className={classNames('ml-[6px] pl-[13px] border-l border-border-token flex flex-col gap-[3px]', !cat.active && 'opacity-50')}>
                        {cat.placements.map((p) => (
                          <label key={p.id} className={classNames('inline-flex items-center text-[12px]', p.enabled ? 'text-text-2' : 'text-muted')}>
                            <Connector />
                            <NodeCheckbox
                              checked={p.enabled}
                              indeterminate={false}
                              disabled={saving || !cat.active}
                              label={`${catLabel(ch, cat)} › ${p.name}`}
                              onChange={(checked) => onSelect({ kind: 'placement', content_channel_id: ch.id, ref_id: p.id, enabled: checked })}
                            />
                            <span className="ml-2 truncate">{p.name}</span>
                          </label>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

// One of the product's videos on the Video card: thumbnail, name, where it
// is, its autopilot switch — and, once on, the channel flow beneath it.
function VideoRow({
  video,
  enabled,
  saving,
  onToggle,
  onSelect,
  linkToDetail = true,
}: {
  video: AutopilotVideo;
  enabled: boolean;
  saving: boolean;
  onToggle: (enabled: boolean) => void;
  onSelect: (sel: AutopilotSelection) => void;
  // Thumbnail + name open the video's detail page (off on the video's own settings).
  linkToDetail?: boolean;
}) {
  const [showChannels, setShowChannels] = useState(false);
  const channels = video.channels || [];
  const activePlacements = channels.reduce(
    (n, ch) => n + ch.categories.reduce((m, cat) => m + cat.placements.filter((p) => p.active).length, 0),
    0,
  );
  const { role } = useUser();
  const { p } = usePortalPath();
  const detailHref = linkToDetail ? pdpVideoHref(isInternal(role), video.id) : null;
  const name = formatVideoName(video.name || video.key || '') || `Video #${video.id}`;
  const length = fmtLength(video.length);
  let switchLabel = enabled ? 'Autopilot on' : 'Autopilot off';
  if (saving) switchLabel = '…';
  let where = 'Not on a page yet';
  if (video.placed_count > 0) where = `Placed on ${video.placed_count} ${video.placed_count === 1 ? 'page' : 'pages'}`;
  else if (video.page_count > 0) where = `On ${video.page_count} ${video.page_count === 1 ? 'page' : 'pages'}, not found there yet`;
  return (
    <Box className={classNames('flex flex-col gap-2 py-2 border-t border-border-token first:border-t-0 min-w-0', !enabled && 'opacity-80')}>
    <Box className="flex items-center gap-3 min-w-0">
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        disabled={saving}
        onClick={() => onToggle(!enabled)}
        title={enabled ? 'On autopilot — its channels are automated. Switch off to stop.' : 'Put this video on autopilot: its channels are automated from here'}
        className={classNames(
          'inline-flex items-center gap-[5px] text-[11px] font-semibold border rounded-[6px] px-2 py-[2px] cursor-pointer disabled:opacity-40 shrink-0',
          enabled ? 'bg-tint text-accent-text border-transparent hover:opacity-90' : 'text-muted border-border-token hover:bg-surface',
        )}
      >
        {switchLabel}
        {enabled && !saving ? <BetaPill solid /> : null}
      </button>
      <a
        href={detailHref ? p(detailHref) : undefined}
        target={detailHref ? '_blank' : undefined}
        rel="noreferrer"
        title={detailHref ? `Open ${name}` : name}
        aria-label={detailHref ? `Open ${name}` : name}
        className={classNames('shrink-0 rounded-[6px]', detailHref && 'hover:opacity-85')}
      >
        <img src={getVideoThumbnail(video)} alt="" className="h-[36px] w-[62px] rounded-[6px] object-cover bg-surface-2 block" />
      </a>
      <Box className="flex flex-col min-w-0">
        <span className="text-[13px] font-semibold text-text-1 truncate" title={name}>
          {detailHref ? (
            <a href={p(detailHref)} target="_blank" rel="noreferrer" className="text-text-1 hover:text-accent-text">
              {name}
            </a>
          ) : (
            name
          )}
          {length ? <span className="text-muted font-normal"> · {length}</span> : null}
        </span>
        <span className="text-[12px] text-text-2">
          {where}
          {enabled && video.autopilot_since ? ` · on autopilot since ${fmtDate(video.autopilot_since)}` : ''}
        </span>
      </Box>
      {enabled ? (
        <button
          type="button"
          className={classNames(btnSecondary, 'ml-auto inline-flex items-center gap-1 shrink-0')}
          onClick={() => setShowChannels((v) => !v)}
          title="Which channels and placements Autopilot automates for this video — check a channel to turn it on"
        >
          Channels · {activePlacements} {activePlacements === 1 ? 'placement' : 'placements'}
          <IconChevronDown size={12} open={showChannels} />
        </button>
      ) : null}
    </Box>
    {enabled && showChannels ? (
      <Box className="rounded-[10px] border border-border-token bg-surface-2 p-3 ml-[8px]">
        <VideoChannelTree channels={channels} saving={saving} onSelect={onSelect} />
      </Box>
    ) : null}
    </Box>
  );
}

function PageRow({
  page,
  brandId,
  enabled,
  saving,
  onToggle,
  desireid,
  onChanged,
}: {
  page: AutopilotPage;
  brandId: number | null;
  // Per-page autopilot switch (optimistic value while saving).
  enabled: boolean;
  saving: boolean;
  onToggle: (enabled: boolean) => void;
  desireid: number;
  onChanged: () => Promise<unknown> | void;
}) {
  // On: the gold chip opens the page's autopilot settings (analysis
  // interval, turn off). Off: one click puts it on autopilot.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { role } = useUser();
  const { p } = usePortalPath();
  // Where the tag's install snippets live: the brand's Settings > Onboarding
  // (staff: that brand's Onboarding tab on its brand page). A retailer page
  // needs the retailer's tag — only staff can open that brand.
  const retailer = needsRetailerTag(page);
  const tagBrandId = (page.tag_brand && page.tag_brand.id) || brandId || 0;
  let onboardingHref: string | null = null;
  if (isInternal(role)) onboardingHref = tagBrandId ? `/brands/${tagBrandId}/onboarding` : null;
  else if (!retailer) onboardingHref = p('/settings/onboarding');
  let switchLabel = enabled ? 'Autopilot on' : 'Autopilot off';
  if (saving) switchLabel = '…';
  return (
    <Box className={classNames('flex flex-col gap-1.5 py-2 border-t border-border-token first:border-t-0', !enabled && 'opacity-80')}>
      <Box className="flex items-center gap-2 flex-wrap min-w-0">
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          disabled={saving}
          onClick={() => (enabled ? setSettingsOpen(true) : onToggle(true))}
          title={enabled ? 'On autopilot — click for its analysis interval, or to turn it off' : 'Put this page on autopilot'}
          className={classNames(
            'inline-flex items-center gap-[5px] text-[11px] font-semibold border rounded-[6px] px-2 py-[2px] cursor-pointer disabled:opacity-40 shrink-0',
            enabled ? 'bg-tint text-accent-text border-transparent hover:opacity-90' : 'text-muted border-border-token hover:bg-surface',
          )}
        >
          {switchLabel}
          {enabled && !saving ? <BetaPill solid /> : null}
        </button>
        <a
          href={page.pdp_url}
          target="_blank"
          rel="noreferrer"
          className="text-[13px] font-semibold text-text-1 hover:text-accent-text truncate max-w-[260px]"
          title={page.pdp_url}
        >
          {page.hostname || page.pdp_url}
        </a>
        {page.retailer ? (
          <span className="text-[11px] font-bold text-text-2 bg-surface-2 rounded-[6px] px-2 py-[3px] uppercase tracking-[0.5px]">
            {page.retailer}
          </span>
        ) : null}
        {/* Off autopilot, the plain Tag installed / Needs tag chip still says whether gt.js is there. */}
        {enabled ? modePill(page) : <PdpTagPill checks={page.checks} compact />}
        <PdpPixelPill checks={page.checks} compact />
      </Box>
      <Box className="flex items-center gap-3 flex-wrap text-[12px] text-text-2">
        {page.markup.live ? (
          <span title="The product markup is published for this page">Markup live · updated {fmtDate(page.markup.updated_at)}</span>
        ) : (
          <span className="text-muted" title="No product markup published for this page yet">Markup pending</span>
        )}
        {page.baseline ? (
          <span title="The score the page had before Autopilot started changing it">
            Baseline {fmtNum(page.baseline.overall)} · {fmtDate(page.baseline.taken_at)}
          </span>
        ) : (
          <span className="text-muted">No baseline yet</span>
        )}
        {enabled && page.autopilot_since ? <span title="When this page was put on autopilot">On autopilot since {fmtDate(page.autopilot_since)}</span> : null}
        {/* How fast we notice changes: the script tag sees every visit on the
            owner's own host; elsewhere a page check looks every few days
            (daily while a change you accepted is awaited). */}
        {enabled ? (
          <span
            className="text-muted"
            title={
              page.tag_capable
                ? 'The script tag can run on this page, so changes are noticed on the next visit; page checks fill in between.'
                : 'Our script cannot run on this host; a page check looks every few days, daily while a change you accepted is awaited.'
            }
          >
            {page.last_observed_at ? `Last checked ${fmtDate(page.last_observed_at)}` : 'Not checked yet'}
            {cadenceNote(page)}
          </span>
        ) : null}

      </Box>
      {/* Embed found → videos found → JSON-LD found, per the last analysis. */}
      <PdpChecksStrip checks={page.checks} compact />
      {enabled && page.mode !== 'on' ? (
        <span className="text-[12.5px] text-text-2">
          {retailer
            ? `This is a retailer page: the script tag goes on ${tagOwnerLabel(page)}. Once the retailer installs it, the page gets live markup and shopper measurement.`
            : 'The script tag is not on this page yet.'}{' '}
          {onboardingHref ? (
            <Link to={onboardingHref} className="font-semibold text-accent-text hover:underline">
              {retailer ? `Open ${page.tag_brand ? page.tag_brand.name : "the retailer"}'s onboarding` : 'Finish onboarding in Settings'}
            </Link>
          ) : null}
        </span>
      ) : null}
      {settingsOpen && (
        <PdpAutopilotDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          desireid={desireid}
          brandId={brandId}
          page={page}
          onChanged={onChanged}
        />
      )}
    </Box>
  );
}


interface SidesProps {
  desireid: number;
  brandId: number | null;
  bundle: ProductAutopilot;
  reload: (silent?: boolean) => Promise<void>;
  // Show only this video on the Video card (the video's own settings).
  onlyVideoId?: number | null;
  // Controlled subtab (the product panel filters its objectives and
  // recommendations by side); uncontrolled when omitted.
  tab?: number;
  onTabChange?: (tab: number) => void;
}

export const TAB_PAGES = 0;
export const TAB_VIDEOS = 1;

export function AutopilotSides({ desireid, brandId, bundle, reload, onlyVideoId = null, tab: tabProp, onTabChange }: SidesProps) {
  // Product page / Videos subtabs; a video's own settings open on Videos.
  const [tabState, setTabState] = useState(onlyVideoId ? TAB_VIDEOS : TAB_PAGES);
  const tab = tabProp != null ? tabProp : tabState;
  const setTab = (next: number) => {
    setTabState(next);
    if (onTabChange) onTabChange(next);
  };
  // Optimistic per-page autopilot switches: the id being saved, reverted on error.
  const [pageOverrides, setPageOverrides] = useState<Record<number, boolean>>({});
  // Optimistic per-video autopilot switches, same pattern.
  const [videoOverrides, setVideoOverrides] = useState<Record<number, boolean>>({});

  // A channel / category / placement checkbox for one video: save, then
  // re-read the bundle (the tree's `active` folds parents in server-side).
  const [selectingId, setSelectingId] = useState<number | null>(null);
  const selectVideoNode = async (fileId: number, sel: AutopilotSelection) => {
    setSelectingId(fileId);
    try {
      await axiosInstant.patch(productAutopilotVideoSelectionsEndpoint(desireid, fileId, brandId), { selections: [sel] });
      await reload(true);
    } catch {
      toast.error('Couldn\'t update this channel setting. Please try again.');
    } finally {
      setSelectingId(null);
    }
  };

  const setVideoAutopilot = async (fileId: number, enabled: boolean) => {
    setVideoOverrides((prev) => ({ ...prev, [fileId]: enabled }));
    try {
      await axiosInstant.patch(productAutopilotVideoEndpoint(desireid, fileId, brandId), { enabled });
      toast.success(enabled ? 'Video on autopilot' : 'Video taken off autopilot');
      await reload(true);
    } catch {
      toast.error('Couldn\'t update autopilot for this video. Please try again.');
    } finally {
      setVideoOverrides((prev) => {
        const next = { ...prev };
        delete next[fileId];
        return next;
      });
    }
  };

  const setPageAutopilot = async (pageId: number, enabled: boolean) => {
    setPageOverrides((prev) => ({ ...prev, [pageId]: enabled }));
    try {
      await axiosInstant.patch(productAutopilotPageEndpoint(desireid, pageId, brandId), { enabled });
      toast.success(enabled ? 'Page on autopilot — analysis rules running' : 'Page taken off autopilot');
      await reload(true);
    } catch (e: any) {
      const code = apiErrorCode(e);
      if (code === 'PdpUrlScoringPaused') toast.error('Activate scoring on this page first (Product Pages tab).');
      else toast.error('Couldn\'t update autopilot for this page. Please try again.');
    } finally {
      setPageOverrides((prev) => {
        const next = { ...prev };
        delete next[pageId];
        return next;
      });
    }
  };


  const pageOn = (pg: AutopilotPage) => (pageOverrides[pg.id] != null ? pageOverrides[pg.id] : pg.autopilot);
  const onPages = bundle.pages.filter(pageOn);
  const allVideos: AutopilotVideo[] = bundle.videos || [];
  const videos = onlyVideoId ? allVideos.filter((v) => v.id === onlyVideoId) : allVideos;
  const videoOn = (v: AutopilotVideo) => (videoOverrides[v.id] != null ? videoOverrides[v.id] : v.autopilot);
  const onVideos = videos.filter(videoOn);
  const pageSide = bundle.channels.find((ch) => ch.key === 'pdp') || { key: 'pdp', name: 'Product page', enabled: true };
  const videoSide = bundle.channels.find((ch) => ch.key === 'video') || { key: 'video', name: 'Video', enabled: true };
  const videoChannels = (videoSide.channels || []).map((c) => c.name).join(', ');
  let videoSubtitle = onlyVideoId
    ? 'Switch this video on and its channels are automated.'
    : 'No video on autopilot yet — switch one on and its channels are automated.';
  if (onVideos.length) {
    videoSubtitle = onlyVideoId
      ? `On autopilot on this product`
      : `${onVideos.length} of ${videos.length} ${videos.length === 1 ? 'video' : 'videos'} on autopilot`;
  }
  if (videoChannels) videoSubtitle += ` · Channels automated per video: ${videoChannels}.`;

  const tabCount = (on: number, all: number) => (all ? ` · ${on}/${all}` : '');
  return (
    <Box className="flex flex-col gap-3">
      <Box className="flex items-center gap-2 flex-wrap">
        <TabHeader
          variant="pill"
          selectedTab={tab}
          setSelectedTab={setTab}
          tabs={[
            { value: TAB_PAGES, text: `Product pages${tabCount(onPages.length, bundle.pages.length)}` },
            { value: TAB_VIDEOS, text: `Videos${tabCount(onVideos.length, videos.length)}` },
          ]}
        />
      </Box>
      {tab === TAB_PAGES ? (
      <ChannelCard
        channel={pageSide}
        subtitle={
          onPages.length
            ? `${onPages.length} of ${bundle.pages.length} ${bundle.pages.length === 1 ? 'page' : 'pages'} on autopilot`
            : 'No page on autopilot yet — switch one on and Autopilot will baseline it and start recommending changes.'
        }
      >
        <Box className="flex flex-col">
          {bundle.pages.map((pg) => (
            <PageRow
              key={pg.id}
              page={pg}
              brandId={brandId}
              enabled={pageOn(pg)}
              saving={pageOverrides[pg.id] != null}
              onToggle={(enabled) => setPageAutopilot(pg.id, enabled)}
              desireid={desireid}
              onChanged={() => reload(true)}
            />
          ))}
          {!bundle.pages.length ? <span className="text-[12.5px] text-muted">No product pages yet.</span> : null}
        </Box>
      </ChannelCard>
      ) : (
      <ChannelCard channel={videoSide} subtitle={videoSubtitle}>
        <Box className="flex flex-col">
          {videos.map((v) => (
            <VideoRow
              key={v.id}
              video={v}
              enabled={videoOn(v)}
              saving={videoOverrides[v.id] != null || selectingId === v.id}
              onToggle={(enabled) => setVideoAutopilot(v.id, enabled)}
              onSelect={(sel) => selectVideoNode(v.id, sel)}
              linkToDetail={!onlyVideoId}
            />
          ))}
          {!videos.length ? (
            <span className="text-[12.5px] text-muted">{onlyVideoId ? 'This video is not on the product yet.' : 'No videos on this product yet.'}</span>
          ) : null}
        </Box>
      </ChannelCard>
      )}
    </Box>
  );
}
